import * as React from "react";
import { StatusPill } from "./PageHeader";
import {
  NO_FILTER,
  applyFilter,
  componentsOf,
  fieldChip,
  fieldText,
  filterActive,
  levelTone,
  orderEntries,
  outOfSequence,
  parseLogRows,
  receiveDelaySeconds,
  timeToMicros,
  timeToMillis,
  type LogEntry,
  type LogFilter,
} from "./logLine";

// LogLines — THE log view. Every place a console shows journal lines renders them with this, so
// they all show the same thing (one source of truth: the admin Logs screen, an app's log in both
// consoles, the tinc network's journal tail).
//
// Why it exists: the panel and every command it runs for another unit now write the one log line
// (internal/logline) — a JSON object. Printed raw in a <pre>, a takeover reads as four hundred
// characters of quoted keys, and the one thing the line was built to carry — WHEN it was written,
// to the microsecond, in an order that holds across nodes — is buried in it while the visible
// timestamp at the start of the row is the journal's (when journald read it, to the second).
//
// A row that is the one line is laid out: its own time (to the millisecond; the full stamp on
// hover and in the detail), the node that wrote it, the level as the kit's status pill, the
// component, the message, and the remaining fields as key=value chips. Click a row for the detail:
// the full time, seq and boot, svc, the journal's receive time and how far behind the line's it is,
// and every field whole.
//
// A row that is NOT the one line — PostgreSQL's journal, tinc's, a tenant's container, a Go runtime
// fatal — is shown exactly as it arrived, in place. Rows are ordered by the line's time, then seq
// inside one boot (logLine.ts orderEntries); a raw row travels with the line before it.

export interface LogLinesProps {
  /** The response's rows: an array of lines, or one newline-separated string. */
  rows: string[] | string | null | undefined;
  /** Shown when there is nothing to show. */
  empty?: React.ReactNode;
  /** A filter chosen in the view's own bar (LogLineFilters). Omit where the view has no bar. */
  filter?: LogFilter;
  /** The scroll box's height class. */
  heightClass?: string;
}

export function LogLines({ rows, empty, filter = NO_FILTER, heightClass = "max-h-[32rem]" }: LogLinesProps) {
  const ordered = React.useMemo(() => orderEntries(parseLogRows(rows)), [rows]);
  const { shown, hiddenRaw } = React.useMemo(() => applyFilter(ordered, filter), [ordered, filter]);
  const unsure = React.useMemo(() => outOfSequence(ordered).length, [ordered]);
  const [open, setOpen] = React.useState<Set<string>>(() => new Set());

  if (ordered.length === 0) {
    return <div className="text-[13px] text-muted">{empty ?? "No lines."}</div>;
  }
  const toggle = (key: string) =>
    setOpen((was) => {
      const next = new Set(was);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  return (
    <div>
      <div className={`${heightClass} overflow-auto rounded-lg border border-line bg-bg py-1 font-mono text-[11.5px] leading-relaxed`}>
        {shown.length === 0 && <div className="px-3 py-1 text-muted">No line matches this filter.</div>}
        {shown.map((e) => {
          const key = e.line ? `${e.line.boot}#${e.line.seq}` : `raw#${e.index}`;
          return e.line ? (
            <ParsedRow key={key} entry={e} open={open.has(key)} onToggle={() => toggle(key)} />
          ) : (
            // Not the one line: as it is, whole — the journal's prefix included.
            <div key={key} className="whitespace-pre-wrap break-words px-3 py-px text-muted">
              {e.raw}
            </div>
          );
        })}
      </div>
      {(hiddenRaw > 0 || unsure > 0) && (
        <div className="mt-1 space-y-0.5 text-[11.5px] text-muted">
          {hiddenRaw > 0 && (
            <div>
              {hiddenRaw} row{hiddenRaw === 1 ? "" : "s"} that carry no level or component (another daemon's lines, or
              unstamped output) are left out by this filter — clear it to see them.
            </div>
          )}
          {unsure > 0 && (
            <div>
              {unsure} line{unsure === 1 ? " is" : "s are"} placed by time against their own sequence number — that node's
              clock moved backwards, or two lines share a microsecond. The detail of a line shows its seq.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ParsedRow({ entry, open, onToggle }: { entry: LogEntry; open: boolean; onToggle: () => void }) {
  const l = entry.line;
  if (!l) return null;
  const faint = l.level === "debug" || l.level === "trace";
  return (
    <div className={open ? "bg-surface2" : undefined}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full flex-wrap items-baseline gap-x-2 gap-y-0.5 px-3 py-px text-left hover:bg-surface2"
      >
        <span className="shrink-0 text-faint" title={timeToMicros(l.time)}>
          {timeToMillis(l.time)}
        </span>
        <span className="shrink-0 text-muted">{l.node}</span>
        <StatusPill tone={levelTone(l.level)}>{l.level || "no level"}</StatusPill>
        {l.decision && <StatusPill tone="accent">decision</StatusPill>}
        {l.component && <span className="shrink-0 text-accent">{l.component}</span>}
        <span className={`min-w-0 whitespace-pre-wrap break-words ${faint ? "text-muted" : "text-fg"}`}>{l.message}</span>
        {!open &&
          l.fields.map(([k, v]) => (
            <span key={k} className="rounded border border-line px-1 text-[10.5px] text-muted" title={`${k}=${fieldText(v)}`}>
              {fieldChip(k, v)}
            </span>
          ))}
      </button>
      {open && <Detail entry={entry} />}
    </div>
  );
}

function Detail({ entry }: { entry: LogEntry }) {
  const l = entry.line;
  if (!l) return null;
  const delay = receiveDelaySeconds(entry);
  const stamp: [string, string][] = [
    ["time", timeToMicros(l.time)],
    ["seq", String(l.seq)],
    ["boot", l.boot],
    ["node", l.node],
    ["svc", l.svc],
  ];
  if (entry.received) {
    // The journal's stamp is to the second, so the difference is too.
    stamp.push([
      "journal received",
      `${entry.received}${delay === undefined ? "" : delay === 0 ? " (the same second)" : ` (${delay} s after it was written)`}`,
    ]);
  }
  if (entry.ident) stamp.push(["journal unit", `${entry.host ?? ""} ${entry.ident}${entry.pid ? `[${entry.pid}]` : ""}`.trim()]);
  return (
    <div className="mx-3 mb-1 mt-0.5 rounded border border-line bg-bg px-3 py-2">
      <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5">
        {stamp.map(([k, v]) => (
          <React.Fragment key={k}>
            <dt className="text-faint">{k}</dt>
            <dd className="min-w-0 break-words text-muted">{v}</dd>
          </React.Fragment>
        ))}
        {l.fields.map(([k, v]) => (
          <React.Fragment key={`f:${k}`}>
            <dt className="text-accent">{k}</dt>
            <dd className="min-w-0 whitespace-pre-wrap break-words text-fg">
              {typeof v === "string" ? v : JSON.stringify(v, null, 2)}
            </dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
}

const barSelect = "rounded-lg border border-line2 bg-bg px-2 py-1 text-[12px] text-fg";

/**
 * LogLineFilters — the level and component choices, for a log view that ALREADY has a bar of
 * controls (the Logs screen's). It is not a bar of its own: a view with no controls gets none.
 * The component choices are the ones present in the rows being shown.
 */
export function LogLineFilters({
  rows,
  value,
  onChange,
}: {
  rows: string[] | string | null | undefined;
  value: LogFilter;
  onChange: (next: LogFilter) => void;
}) {
  const entries = React.useMemo(() => parseLogRows(rows), [rows]);
  // The chosen component stays a choice even when a refresh brings rows without it — otherwise the
  // select would read "every component" while the filter still held the old one.
  const components = React.useMemo(() => {
    const present = componentsOf(entries);
    return value.component && !present.includes(value.component) ? [...present, value.component].sort() : present;
  }, [entries, value.component]);
  // Nothing here is the one line (another daemon's journal): there is no level to choose.
  if (!entries.some((e) => e.line)) return null;
  return (
    <>
      <select
        aria-label="Level"
        className={barSelect}
        value={value.level}
        onChange={(e) => onChange({ ...value, level: e.target.value as LogFilter["level"] })}
      >
        <option value="">every level</option>
        <option value="info">info and above</option>
        <option value="warn">warn and above</option>
        <option value="error">error and above</option>
        <option value="decision">decisions only</option>
      </select>
      <select
        aria-label="Component"
        className={barSelect}
        value={value.component}
        onChange={(e) => onChange({ ...value, component: e.target.value })}
      >
        <option value="">every component</option>
        {components.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      {filterActive(value) && (
        <button type="button" className="text-[12px] text-muted underline hover:text-fg" onClick={() => onChange(NO_FILTER)}>
          clear
        </button>
      )}
    </>
  );
}
